"use client"

import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Calendar, Mic, Keyboard, PenTool } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'

interface PostCapturePopupProps {
    imageUrl: string
    /** Initial date taken (ISO string); defaults to now */
    initialTakenAt: string
    onTakenAtChange?: (takenAt: string) => void
    onVoiceRecord: (takenAt: string) => void
    onTextInput: (takenAt: string) => void
    onEdit?: () => void // Placeholder for future edit functionality
    onClose: (takenAt: string) => void
    autoCloseDelay?: number // Default 6 seconds
}

/**
 * Post-capture popup overlay that appears for 6 seconds after photo is taken.
 * Shows date taken, description actions (voice/keyboard), and edit (placeholder).
 */
export default function PostCapturePopup({
    imageUrl,
    initialTakenAt,
    onTakenAtChange,
    onVoiceRecord,
    onTextInput,
    onEdit,
    onClose,
    autoCloseDelay = 6000,
}: PostCapturePopupProps) {
    const [timeRemaining, setTimeRemaining] = useState(autoCloseDelay)
    const [takenAt, setTakenAt] = useState<string>(() => {
        try {
            const d = new Date(initialTakenAt)
            if (!Number.isNaN(d.getTime())) return d.toISOString()
        } catch { /* ignore */ }
        return new Date().toISOString()
    })
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const isClosedRef = useRef(false) // Track if popup was closed by user action
    const takenAtRef = useRef(takenAt)
    takenAtRef.current = takenAt

    // Sync from parent when initialTakenAt changes
    useEffect(() => {
        try {
            const d = new Date(initialTakenAt)
            if (!Number.isNaN(d.getTime())) setTakenAt(d.toISOString())
        } catch { /* ignore */ }
    }, [initialTakenAt])

    const handleTakenAtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        if (!value) return
        const iso = new Date(value).toISOString()
        setTakenAt(iso)
        onTakenAtChange?.(iso)
    }
    
    const onCloseRef = useRef(onClose)
    useEffect(() => {
        onCloseRef.current = onClose
    }, [onClose])

    useEffect(() => {
        isClosedRef.current = false // Reset when component mounts
        timerRef.current = setInterval(() => {
            setTimeRemaining((prev) => {
                if (prev <= 100) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current)
                    }
                    // Only call onClose if popup wasn't closed by user action
                    if (!isClosedRef.current) {
                        const currentTakenAt = takenAtRef.current
                        setTimeout(() => {
                            onCloseRef.current(currentTakenAt)
                        }, 0)
                    }
                    return 0
                }
                return prev - 100
            })
        }, 100)

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current)
            }
        }
    }, [])

    const handleVoiceRecord = () => {
        isClosedRef.current = true // Mark as closed by user action
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        onVoiceRecord(takenAtRef.current)
    }

    const handleTextInput = () => {
        isClosedRef.current = true // Mark as closed by user action
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
        onTextInput(takenAtRef.current)
    }

    // Format ISO for datetime-local (local time)
    const toDatetimeLocal = (iso: string) => {
        try {
            const d = new Date(iso)
            if (Number.isNaN(d.getTime())) return ''
            const y = d.getFullYear()
            const m = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')
            const h = String(d.getHours()).padStart(2, '0')
            const min = String(d.getMinutes()).padStart(2, '0')
            return `${y}-${m}-${day}T${h}:${min}`
        } catch { return '' }
    }

    const content = (
        <div className="fixed inset-0 z-[110] bg-black/60 flex flex-col" style={{ pointerEvents: 'auto' }}>
            {/* Photo preview - flex-1 so it shrinks when bottom bar is present */}
            <div className="flex-1 flex items-center justify-center min-h-0 overflow-auto">
                <div className="relative">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={imageUrl}
                        alt="Captured photo"
                        className="max-w-full max-h-[65vh] object-contain"
                    />
                    {/* Progress bar */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 w-64 h-1 bg-white/30 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-white transition-all duration-100"
                            style={{ width: `${(timeRemaining / autoCloseDelay) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Bottom bar: Description (voice/text) and Date taken - always visible, above safe area */}
            <div className="flex-shrink-0 w-full flex flex-col gap-3 items-center px-4 pt-4 pb-10 bg-gradient-to-t from-black/70 to-transparent">
                {/* Combined Description Button - primary action first */}
                <div
                    className="flex gap-4 items-center justify-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-white hover:bg-gray-100 shadow-lg rounded-full flex items-center overflow-hidden">
                        <button
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleVoiceRecord()
                            }}
                            className="flex items-center gap-3 px-6 py-3 border-r border-gray-300 hover:bg-gray-50 transition-colors"
                            aria-label="Record voice description"
                        >
                            <Mic className="w-6 h-6 text-primary" />
                            <span className="text-primary font-medium">Description</span>
                        </button>
                        <button
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleTextInput()
                            }}
                            className="px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-center"
                            aria-label="Type description"
                        >
                            <Keyboard className="w-6 h-6 text-primary" />
                        </button>
                    </div>
                    {onEdit && (
                        <Button
                            onClick={onEdit}
                            size="lg"
                            className="rounded-full w-16 h-16 bg-white hover:bg-gray-100 shadow-lg flex items-center justify-center opacity-50"
                            aria-label="Edit photo (coming soon)"
                            disabled
                        >
                            <PenTool className="w-8 h-8 text-primary" />
                        </Button>
                    )}
                </div>

                {/* Date taken */}
                <div className="flex items-center gap-2 bg-white/95 backdrop-blur rounded-lg px-3 py-2 shadow-lg">
                    <Calendar className="w-4 h-4 text-primary flex-shrink-0" aria-hidden />
                    <input
                        type="datetime-local"
                        value={toDatetimeLocal(takenAt)}
                        onChange={handleTakenAtChange}
                        className="bg-transparent text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
                        aria-label="Date taken"
                    />
                </div>
            </div>
        </div>
    )

    // Use portal to render above Dialog overlays
    if (typeof window !== 'undefined') {
        return createPortal(content, document.body)
    }
    return content
}

